import apiClient from '../../../services/apiClient';

// AI interaction over a slide: "Explain more" and free-form Q&A (RAG), plus the
// mic-driven transcription used by the Ask panel.

// The four cached explanation variants. Same ids and the same four words as the
// AI Tutor's mode chips (see TutorMode) — one vocabulary across both tabs.
export type ExplainVariant = 'explain' | 'deeper' | 'eli5' | 'example';

export interface ExplainRequest {
  lessonId: string;
  slideIndex: number;
  // Defaults to 'explain' server-side.
  variant?: ExplainVariant;
}

export interface ExplainResponse {
  // Plain-text flattening of `html`, derived server-side. Read-aloud speaks
  // this, and it is what renders if `html` is ever absent.
  text: string;
  // Rich HTML from the cache. UNTRUSTED — it is model output, and a slide's own
  // text is part of the prompt that produced it. Render it ONLY through
  // <RichExplanation>, which sanitises with DOMPurify first; never pass it to
  // dangerouslySetInnerHTML directly.
  html?: string;
  format?: 'html' | 'markdown';
  variant?: ExplainVariant;
  // True when this was served from acechem_ai_explanations rather than generated
  // for this request. Surfaced so the UI can say the content is a stable,
  // reviewed explanation rather than a fresh one-off answer.
  cached?: boolean;
  model?: string;
  generatedAt?: string | null;
}

export interface AskRequest {
  lessonId: string;
  slideIndex: number;
  query: string;
}

// A rich RAG citation: links back to its source slide and previews the chunk so
// students see a meaningful source, not a bare chunk UUID. Legacy responses may
// still send bare-string ids, so consumers stay tolerant of `string` too.
export interface Citation {
  id: string;
  // 0-based slide index within the lesson for slide chunks, else null (lesson-level).
  slideIndex: number | null;
  // Source slide's heading, else null (lesson-level chunk).
  heading: string | null;
  // First ~140 chars of the chunk text.
  snippet: string;
  sourceType: 'slide' | 'lesson';
}

export interface AskResponse {
  text: string;
  // Retrieved chunks that grounded the answer (RAG citations). Rich objects, but
  // tolerant of legacy bare-string chunk ids.
  citations?: Array<Citation | string>;
}

export interface TranscribeResponse {
  text: string;
}

// Explanation of the current slide, served from the pre-generated HTML cache
// (BP 11). Generated once per (slide content × variant × model × prompt
// version), so every student gets the same reviewed answer and it costs nothing
// to serve. A cache miss generates it live and stores it, so this never fails
// merely because a slide is new or was edited.
export async function explainSlide(req: ExplainRequest): Promise<ExplainResponse> {
  const { data } = await apiClient.post<ExplainResponse>('/api/v1/ai/explain', req);
  return data;
}

// Grounded Q&A against the lesson/course knowledge base.
export async function askQuestion(req: AskRequest): Promise<AskResponse> {
  const { data } = await apiClient.post<AskResponse>('/api/v1/ai/ask', req);
  return { text: data.text, citations: data.citations ?? [] };
}

// Speech-to-text for mic Q&A. Sends the recorded blob as multipart 'audio'.
export async function transcribeAudio(blob: Blob): Promise<TranscribeResponse> {
  const form = new FormData();
  // Name the part 'audio' per the contract; extension hints the codec.
  const ext = blob.type.includes('mp4') ? 'mp4' : blob.type.includes('wav') ? 'wav' : 'webm';
  form.append('audio', blob, `question.${ext}`);
  const { data } = await apiClient.post<TranscribeResponse>('/api/v1/stt/transcribe', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}
